import React, { useEffect, useState } from 'react';
import { attendanceAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiSearch, FiSave, FiUsers } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import Select from 'react-select';

const StaffAttendance = () => {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [attendanceExists, setAttendanceExists] = useState(false);
  const [isHoliday, setIsHoliday] = useState(false);
  
  const [searchCriteria, setSearchCriteria] = useState({
    role_id: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await attendanceAPI.getRoles();
      if (response.data.success) {
        setRoles(response.data.data);
      }
    } catch (error) {
      toast.error('Error fetching roles');
      console.error(error);
    }
  };

  const handleSearch = async () => {
    if (!searchCriteria.role_id) {
      toast.error('Please select a role');
      return;
    }

    setLoading(true);
    try {
      const response = await attendanceAPI.getStaffByRole(searchCriteria);
      if (response.data.success) {
        setStaffList(response.data.data);
        setAttendanceExists(response.data.attendance_exists);
        setIsHoliday(false);
        
        if (response.data.data.length === 0) {
          toast.info('No staff found for this role');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching staff');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAttendanceChange = (staffId, type) => {
    setStaffList(prevList =>
      prevList.map(staff =>
        staff.id === staffId
          ? { ...staff, attendance_type: type }
          : staff
      )
    );
  };

  const handleRemarkChange = (staffId, remark) => {
    setStaffList(prevList =>
      prevList.map(staff =>
        staff.id === staffId
          ? { ...staff, remark: remark }
          : staff
      )
    );
  };

  const handleHolidayToggle = () => {
    if (!isHoliday) {
      const confirmed = window.confirm('Are you sure you want to mark this as a holiday?');
      if (confirmed) {
        setIsHoliday(true);
      }
    } else {
      setIsHoliday(false);
      // Reset all to Present
      setStaffList(prevList =>
        prevList.map(staff => ({ ...staff, attendance_type: 1 }))
      );
    }
  };

  const handleSaveAttendance = async () => {
    if (staffList.length === 0) {
      toast.error('No staff to save attendance for');
      return;
    }

    setSubmitting(true);
    try {
      const attendanceData = staffList.map(staff => ({
        staff_id: staff.id,
        attendance_type: staff.attendance_type,
        remark: staff.remark || '',
        attendance_id: staff.attendance_id
      }));

      const response = await attendanceAPI.saveAttendance({
        role_id: searchCriteria.role_id,
        date: searchCriteria.date,
        attendance: attendanceData
      });

      if (response.data.success) {
        toast.success(response.data.message);
        // Refresh the list to get updated attendance IDs
        handleSearch();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving attendance');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const roleOptions = roles.map(role => ({
    value: role.id,
    label: role.name
  }));

  const getAttendanceLabel = (type) => {
    switch (type) {
      case 1: return 'Present';
      case 2: return 'Late';
      case 3: return 'Absent';
      case 4: return 'Half Day';
      default: return 'Present';
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
          <FiUsers className="inline mr-2" /> Human Resource
        </h1>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        {/* Search Criteria Section */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            <FiSearch className="inline mr-2" /> Select Criteria
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role
              </label>
              <Select
                options={roleOptions}
                value={roleOptions.find(opt => opt.value === searchCriteria.role_id)}
                onChange={(option) => setSearchCriteria({ ...searchCriteria, role_id: option ? option.value : '' })}
                placeholder="Select"
                isClearable
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attendance Date
              </label>
              <input
                type="date"
                value={searchCriteria.date}
                onChange={(e) => setSearchCriteria({ ...searchCriteria, date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
              />
            </div>
          </div>
          <div className="mt-4">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-md transition-colors"
            >
              <FiSearch className="inline mr-2" />
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>

        {/* Staff List Section */}
        {staffList.length > 0 && (
          <>
            {attendanceExists && (
              <div className="px-6 py-3 bg-green-50 border-b border-green-200">
                <p className="text-green-800 text-sm">
                  Attendance Already Submitted. You Can Edit Record
                </p>
              </div>
            )}

            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  <FiUsers className="inline mr-2" /> Staff List
                </h3>
                <div className="flex gap-3">
                  <button
                    onClick={handleHolidayToggle}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      isHoliday
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    {isHoliday ? '✓ ' : ''}Mark As Holiday
                  </button>
                  <button
                    onClick={handleSaveAttendance}
                    disabled={submitting}
                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-md transition-colors"
                  >
                    <FiSave className="inline mr-2" />
                    {submitting ? 'Saving...' : 'Save Attendance'}
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">#</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Staff ID</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Role</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Attendance</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staffList.map((staff, index) => (
                      <tr key={staff.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600">{index + 1}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{staff.employee_id}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {staff.name} {staff.surname}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{staff.role_name}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-4">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="radio"
                                name={`attendance_${staff.id}`}
                                value="1"
                                checked={staff.attendance_type === 1}
                                onChange={() => handleAttendanceChange(staff.id, 1)}
                                disabled={isHoliday}
                                className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-700">Present</span>
                            </label>
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="radio"
                                name={`attendance_${staff.id}`}
                                value="2"
                                checked={staff.attendance_type === 2}
                                onChange={() => handleAttendanceChange(staff.id, 2)}
                                disabled={isHoliday}
                                className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-700">Late</span>
                            </label>
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="radio"
                                name={`attendance_${staff.id}`}
                                value="3"
                                checked={staff.attendance_type === 3}
                                onChange={() => handleAttendanceChange(staff.id, 3)}
                                disabled={isHoliday}
                                className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-700">Absent</span>
                            </label>
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="radio"
                                name={`attendance_${staff.id}`}
                                value="4"
                                checked={staff.attendance_type === 4}
                                onChange={() => handleAttendanceChange(staff.id, 4)}
                                disabled={isHoliday}
                                className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-700">Half Day</span>
                            </label>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <input
                            type="text"
                            value={staff.remark}
                            onChange={(e) => handleRemarkChange(staff.id, e.target.value)}
                            placeholder="Add note"
                            className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {!loading && staffList.length === 0 && searchCriteria.role_id && (
          <div className="p-12 text-center">
            <FiUsers size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Staff Found</h3>
            <p className="text-gray-600">
              No staff members found for the selected role. Try selecting a different role.
            </p>
          </div>
        )}

        {!searchCriteria.role_id && staffList.length === 0 && (
          <div className="p-12 text-center">
            <FiSearch size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Select Criteria</h3>
            <p className="text-gray-600">
              Please select a role and date to view staff attendance.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffAttendance;
